// Team & access — the org user manager (admins/owners only).
//
// Three surfaces:
//   1. Members: org roles (member ↔ admin; the owner is untouchable),
//      removal, and selection for the access matrix.
//   2. Invites: email + org role + optional per-project assignments —
//      provisioned automatically by the claims worker on acceptance.
//   3. Project access matrix for the selected member: which projects they
//      see (participant doc + deal.memberUids stay in sync in one batch),
//      their role per project, and per-power overrides.
//
// Org admins/owners implicitly see every project with every power — the
// matrix is for plain members. The generated Firestore rules enforce all of
// this server-side; this page is just the steering wheel.
import React, { useEffect, useMemo, useState } from 'react';
import firebase, { db } from '../firebaseConfig';
import Modal from '../components/Modal';
import SupportAccessCard from '../components/SupportAccessCard';
import { useToast } from '../components/Toast';
import { useAuth } from '../AuthContext';
import { useOrg, createInvite } from '../data/orgs';
import { useEntityList } from '../data/entities';
import {
  useVisibleDeals,
  DEAL_ROLES,
  DEAL_POWERS,
  POWER_LABELS,
  DEAL_ROLE_POWERS,
  DEFAULT_DEAL_ROLE,
  effectivePowers,
} from '../data/permissions';

const ROLE_TONE = { owner: 'b-warn', admin: 'b-info', member: '' };
const ROLE_HINT = {
  viewer: 'sees the project, nothing else',
  commenter: 'view + download + comment',
  editor: 'view, download, upload, edit, AI',
  approver: 'editor + approvals',
  admin: 'everything incl. managing people',
};

const fmtWhen = (ms) => (ms ? new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

// ---- Invite modal ------------------------------------------------------------------
function InviteModal({ org, orgId, deals, onClose }) {
  const { user } = useAuth();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [assign, setAssign] = useState({}); // dealId → dealRole
  const [busy, setBusy] = useState(false);

  const toggleProject = (dealId) =>
    setAssign((cur) => {
      const next = { ...cur };
      if (next[dealId]) delete next[dealId];
      else next[dealId] = DEFAULT_DEAL_ROLE;
      return next;
    });

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const projects = Object.entries(assign).map(([dealId, dealRole]) => ({
        dealId,
        dealName: deals.find((d) => d.id === dealId)?.name || '',
        dealRole,
        partyKey: 'team',
      }));
      await createInvite(org || orgId, email, role, user, projects);
      toast(`Invite sent to ${email.trim()}`, 'ok');
      onClose();
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Invite a user" onClose={onClose}>
      <form className="u-stack" style={{ '--gap': 'var(--sp-4)' }} onSubmit={submit}>
        <div className="cs-field">
          <label htmlFor="inv-email">Email</label>
          <input
            id="inv-email"
            className="cs-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="colleague@company.com"
            autoFocus
          />
        </div>
        <div className="cs-field">
          <label htmlFor="inv-role">Organization role</label>
          <select id="inv-role" className="cs-input" value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="member">Member — sees only assigned projects</option>
            <option value="admin">Admin — sees and manages everything</option>
          </select>
        </div>
        {role === 'member' && (
          <div className="cs-field">
            <label>Project access (optional — provisioned when they accept)</label>
            <div className="u-stack" style={{ '--gap': 'var(--sp-2)', maxHeight: 260, overflowY: 'auto' }}>
              {(deals || []).map((d) => (
                <div key={d.id} className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'center' }}>
                  <label className="u-cluster u-small" style={{ '--gap': 'var(--sp-2)', flex: 1, cursor: 'pointer' }}>
                    <input type="checkbox" checked={Boolean(assign[d.id])} onChange={() => toggleProject(d.id)} />
                    <span>{d.name}</span>
                  </label>
                  {assign[d.id] && (
                    <select
                      className="cs-input"
                      style={{ width: 130, padding: '2px 6px' }}
                      value={assign[d.id]}
                      onChange={(e) => setAssign((cur) => ({ ...cur, [d.id]: e.target.value }))}
                    >
                      {DEAL_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  )}
                </div>
              ))}
              {!(deals || []).length && <span className="u-small u-muted">No projects yet.</span>}
            </div>
          </div>
        )}
        <div className="u-cluster" style={{ '--gap': 'var(--sp-3)', justifyContent: 'flex-end' }}>
          <button type="button" className="cs-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="cs-btn cs-btn-primary" disabled={busy || !email.trim()}>
            {busy ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---- Per-member project access matrix ----------------------------------------------
function AccessMatrix({ orgId, member, deals }) {
  const toast = useToast();
  const [grants, setGrants] = useState(null); // dealId → participant data | null
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      const entries = await Promise.all(
        (deals || []).map(async (d) => {
          try {
            const snap = await db.doc(`orgs/${orgId}/deals/${d.id}/participants/${member.uid}`).get();
            return [d.id, snap.exists ? snap.data() : null];
          } catch {
            return [d.id, null];
          }
        })
      );
      if (alive) setGrants(Object.fromEntries(entries));
    })();
    return () => { alive = false; };
  }, [orgId, member.uid, deals, refresh]);

  const grant = async (deal) => {
    try {
      const batch = db.batch();
      batch.set(db.doc(`orgs/${orgId}/deals/${deal.id}/participants/${member.uid}`), {
        uid: member.uid,
        email: member.email || '',
        displayName: member.displayName || '',
        partyKey: 'team',
        dealRole: DEFAULT_DEAL_ROLE,
        addedBy: member.addedBy || member.uid,
        createdAt: Date.now(),
      });
      batch.update(db.doc(`orgs/${orgId}/deals/${deal.id}`), {
        memberUids: firebase.firestore.FieldValue.arrayUnion(member.uid),
        updatedAt: Date.now(),
      });
      await batch.commit();
      setRefresh((n) => n + 1);
      toast(`${member.displayName || member.email} can upload, edit, and request changes on "${deal.name}"`, 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    }
  };

  const revoke = async (deal) => {
    try {
      const batch = db.batch();
      batch.delete(db.doc(`orgs/${orgId}/deals/${deal.id}/participants/${member.uid}`));
      batch.update(db.doc(`orgs/${orgId}/deals/${deal.id}`), {
        memberUids: firebase.firestore.FieldValue.arrayRemove(member.uid),
        updatedAt: Date.now(),
      });
      await batch.commit();
      setRefresh((n) => n + 1);
      toast(`Access to "${deal.name}" removed`, 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    }
  };

  const setRole = async (deal, dealRole) => {
    try {
      await db.doc(`orgs/${orgId}/deals/${deal.id}/participants/${member.uid}`).update({
        dealRole,
        powers: {}, // role change resets overrides — presets take over
        updatedAt: Date.now(),
      });
      setRefresh((n) => n + 1);
    } catch (err) {
      toast(err.message, 'danger');
    }
  };

  const togglePower = async (deal, p) => {
    const g = grants?.[deal.id];
    if (!g) return;
    const eff = effectivePowers(g.dealRole, g.powers);
    const next = { ...(g.powers || {}), [p]: !eff[p] };
    // Drop overrides that match the preset again.
    if (next[p] === (DEAL_ROLE_POWERS[g.dealRole] || {})[p]) delete next[p];
    try {
      await db.doc(`orgs/${orgId}/deals/${deal.id}/participants/${member.uid}`).update({
        powers: next,
        updatedAt: Date.now(),
      });
      setRefresh((n) => n + 1);
    } catch (err) {
      toast(err.message, 'danger');
    }
  };

  if (member.role === 'admin' || member.role === 'owner') {
    return (
      <div className="cs-card" style={{ padding: 'var(--sp-4)' }}>
        <span className="u-small u-muted">
          {member.displayName || member.email} is an org {member.role} — they see every {member.role === 'owner' ? '' : 'non-restricted '}project with full powers. Set them to
          “member” to scope their access per project.
        </span>
      </div>
    );
  }
  if (grants === null) return <div className="cs-skeleton" style={{ height: 120 }} />;

  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
      {(deals || []).map((d) => {
        const g = grants[d.id];
        const eff = g ? effectivePowers(g.dealRole, g.powers) : null;
        return (
          <div key={d.id} className="cs-card" style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
            <div className="u-cluster" style={{ '--gap': 'var(--sp-3)', alignItems: 'center' }}>
              <label className="u-cluster" style={{ '--gap': 'var(--sp-2)', flex: 1, cursor: 'pointer', minWidth: 220 }}>
                <input
                  type="checkbox"
                  checked={Boolean(g)}
                  onChange={() => (g ? revoke(d) : grant(d))}
                  aria-label={`Toggle access to ${d.name}`}
                />
                <strong className="u-small">{d.name}</strong>
              </label>
              {g && (
                <>
                  <select
                    className="cs-input"
                    style={{ width: 128, padding: '2px 6px' }}
                    value={g.dealRole}
                    onChange={(e) => setRole(d, e.target.value)}
                    title={ROLE_HINT[g.dealRole]}
                  >
                    {DEAL_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <div className="u-cluster" style={{ '--gap': 'var(--sp-1)' }}>
                    {DEAL_POWERS.map((p) => (
                      <button
                        key={p}
                        type="button"
                        className="cs-chip"
                        onClick={() => togglePower(d, p)}
                        title={`${POWER_LABELS[p]} — click to ${eff[p] ? 'revoke' : 'grant'}`}
                        style={{
                          cursor: 'pointer',
                          opacity: eff[p] ? 1 : 0.42,
                          borderColor: eff[p] ? 'var(--ok)' : 'var(--line)',
                          textDecoration: eff[p] ? 'none' : 'line-through',
                        }}
                      >
                        {POWER_LABELS[p]}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {!g && <span className="u-xs u-muted">no access</span>}
            </div>
          </div>
        );
      })}
      {!(deals || []).length && <span className="u-small u-muted">No projects yet.</span>}
    </div>
  );
}

// ---- Letterhead theme --------------------------------------------------------------
// The org's branded-export theme (orgs/{id}.brandTheme) — consumed by the
// export letterhead path (clean Word copies on letterhead). Colors are hex
// WITHOUT '#' (OOXML convention); the pickers handle the translation.
const hexIn = (v, fallback) => `#${String(v || fallback).replace('#', '')}`;
const hexOut = (v) => String(v || '').replace('#', '').toUpperCase();
function LetterheadCard({ orgId, org }) {
  const toast = useToast();
  const t = org?.brandTheme || {};
  const [form, setForm] = useState({
    entity: t.entity || org?.name || '',
    tagline: t.tagline || '',
    legend: t.legend ?? 'PRIVATE AND CONFIDENTIAL',
    bandColor: hexIn(t.bandColor, '1F2430'),
    headingColor: hexIn(t.headingColor, '1F2430'),
    borderColor: hexIn(t.borderColor, '9AA0A6'),
    taglineColor: hexIn(t.taglineColor, 'FFFFFF'),
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      await db.doc(`orgs/${orgId}`).set({
        brandTheme: {
          ...t,
          entity: form.entity.trim(),
          tagline: form.tagline.trim(),
          legend: form.legend.trim(),
          bandColor: hexOut(form.bandColor),
          headingColor: hexOut(form.headingColor),
          borderColor: hexOut(form.borderColor),
          taglineColor: hexOut(form.taglineColor),
        },
        updatedAt: Date.now(),
      }, { merge: true });
      toast('Letterhead theme saved — used by "Letterhead clean copies" in package exports', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cs-card u-stack" style={{ padding: 'var(--sp-4)', '--gap': 'var(--sp-3)' }}>
      {/* Live preview of the letterhead band */}
      <div style={{ background: form.bandColor, borderRadius: 4, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ textAlign: 'left' }}>
          <strong style={{ color: form.taglineColor, fontSize: 12, letterSpacing: '0.08em', textTransform: t.taglineCaps === false ? 'none' : 'uppercase', display: 'block' }}>
            {form.tagline || form.entity || 'Your organization'}
          </strong>
          {t.website && (
            <span style={{ color: t.websiteColor ? `#${String(t.websiteColor).replace('#', '')}` : form.taglineColor, fontSize: 11 }}>
              {t.website}
            </span>
          )}
        </span>
        <span style={{ color: form.taglineColor, fontSize: 10, opacity: 0.8 }}>{form.legend}</span>
      </div>
      <div className="u-cluster" style={{ '--gap': 'var(--sp-3)' }}>
        <div className="cs-field" style={{ flex: 1, minWidth: 220 }}>
          <label htmlFor="lh-entity">Legal entity (footer copyright)</label>
          <input id="lh-entity" className="cs-input" value={form.entity} onChange={set('entity')} />
        </div>
        <div className="cs-field" style={{ flex: 1, minWidth: 220 }}>
          <label htmlFor="lh-tagline">Band tagline</label>
          <input id="lh-tagline" className="cs-input" value={form.tagline} onChange={set('tagline')} placeholder="Company · domain.com" />
        </div>
        <div className="cs-field" style={{ flex: 1, minWidth: 220 }}>
          <label htmlFor="lh-legend">Per-page legend</label>
          <input id="lh-legend" className="cs-input" value={form.legend} onChange={set('legend')} placeholder="Empty = no legend" />
        </div>
      </div>
      <div className="u-cluster" style={{ '--gap': 'var(--sp-4)' }}>
        {[['bandColor', 'Band'], ['headingColor', 'Headings'], ['borderColor', 'Border'], ['taglineColor', 'Band text']].map(([k, label]) => (
          <label key={k} className="u-small u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'center', cursor: 'pointer' }}>
            <input type="color" value={form[k]} onChange={set(k)} style={{ width: 34, height: 26, padding: 0, border: '1px solid var(--line)', borderRadius: 4, background: 'none', cursor: 'pointer' }} />
            {label}
          </label>
        ))}
      </div>
      <div className="u-split">
        <span className="u-xs u-muted">
          Applied when "Letterhead clean copies" is ticked in a package export (contents table, band, legend, and
          footer come with it). A logo can be added by your agent (brandTheme.logoS3Key).
        </span>
        <button className="cs-btn cs-btn-primary" disabled={busy || !form.entity.trim()} onClick={save}>
          {busy ? 'Saving…' : 'Save theme'}
        </button>
      </div>
    </div>
  );
}

// ---- Page ------------------------------------------------------------------------
// ---- Connected apps -------------------------------------------------------------------
// OAuth grants the signed-in user gave to external apps (ChatGPT via the MCP
// connector — backend/api/oauthServer.js). Personal, not org-scoped: a grant
// lets the app act as THIS user wherever they already have access. Revoking
// writes revokedAt under rules; the backend checks it on every call.
function ConnectedAppsCard({ user }) {
  const toast = useToast();
  const [grants, setGrants] = useState(null);
  useEffect(() => {
    if (!user?.uid) return undefined;
    const unsub = db.collection('oauthGrants').where('uid', '==', user.uid)
      .onSnapshot(
        (snap) => setGrants(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))),
        () => setGrants([])
      );
    return () => unsub();
  }, [user?.uid]);
  const revoke = async (g) => {
    try {
      await db.doc(`oauthGrants/${g.id}`).set({ revokedAt: Date.now(), revokedBy: 'user' }, { merge: true });
      toast(`${g.clientName || 'App'} disconnected`, 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    }
  };
  const live = (grants || []).filter((g) => !g.revokedAt);
  const past = (grants || []).filter((g) => g.revokedAt);
  return (
    <div className="cs-card u-stack" style={{ '--gap': 'var(--sp-3)' }}>
      <p className="u-small u-muted" style={{ margin: 0 }}>
        External apps connected to <strong>your</strong> account — ChatGPT acts as you inside the projects you can
        already reach, and every action is recorded in the audit trail as “you via ChatGPT”.
      </p>
      {grants === null && <span className="u-small u-muted">Loading…</span>}
      {grants !== null && !live.length && <div className="cs-empty">No connected apps. In ChatGPT: Settings → Apps &amp; Connectors → add fast.legal.</div>}
      {live.map((g) => (
        <div key={g.id} className="u-cluster" style={{ '--gap': 'var(--sp-3)', justifyContent: 'space-between' }}>
          <span className="u-stack" style={{ '--gap': '2px' }}>
            <strong>{g.clientName || g.clientId}</strong>
            <span className="u-small u-muted">
              {(g.scopes || []).join(' · ')} · connected {fmtWhen(g.createdAt)}
              {g.lastUsedAt ? ` · last used ${fmtWhen(g.lastUsedAt)}` : ''}
            </span>
          </span>
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => revoke(g)}>Disconnect</button>
        </div>
      ))}
      {past.length > 0 && (
        <span className="u-small u-muted">{past.length} disconnected app{past.length === 1 ? '' : 's'} in history.</span>
      )}
    </div>
  );
}

export default function Team() {
  const { user } = useAuth();
  const { org, orgId, role } = useOrg();
  const toast = useToast();
  const { deals } = useVisibleDeals();
  const { docs: members } = useEntityList('member', orgId ? { org: orgId } : null, { enabled: !!orgId });
  const [invites, setInvites] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [selectedUid, setSelectedUid] = useState(null);

  const isAdmin = role === 'admin' || role === 'owner';

  useEffect(() => {
    if (!orgId || !isAdmin) return undefined;
    const unsub = db.collection('invites').where('orgId', '==', orgId).onSnapshot(
      (snap) => setInvites(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setInvites([])
    );
    return () => unsub();
  }, [orgId, isAdmin]);

  const selected = useMemo(
    () => (members || []).find((m) => m.uid === selectedUid) || null,
    [members, selectedUid]
  );

  const setOrgRole = async (m, newRole) => {
    try {
      await db.doc(`orgs/${orgId}/members/${m.uid}`).update({ role: newRole });
      toast(`${m.displayName || m.email} is now an org ${newRole}`, 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    }
  };

  // Delegated inviting: a member with canInvite may invite outside emails
  // onto projects they admin (project Team tab) — no org-wide visibility.
  const setCanInvite = async (m, canInvite) => {
    try {
      await db.doc(`orgs/${orgId}/members/${m.uid}`).update({ canInvite });
      toast(canInvite ? `${m.displayName || m.email} can now invite people to projects they admin` : `${m.displayName || m.email} can no longer send invites`, 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    }
  };

  const removeMember = async (m) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${m.displayName || m.email} from ${org?.name}? They lose access to every project.`)) return;
    try {
      await db.doc(`orgs/${orgId}/members/${m.uid}`).delete();
      if (selectedUid === m.uid) setSelectedUid(null);
      toast('Member removed', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    }
  };

  const revokeInvite = async (inv) => {
    try {
      await db.doc(`invites/${inv.id}`).set({ status: 'revoked' }, { merge: true });
      toast('Invite revoked', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    }
  };

  if (!isAdmin) {
    return (
      <div className="cs-page">
        <div className="cs-card" style={{ padding: 'var(--sp-6)' }}>
          <h2>Team</h2>
          <p className="u-muted">Only organization admins can manage users and project access.</p>
        </div>
      </div>
    );
  }

  const pendingInvites = (invites || []).filter((i) => ['pending', 'accepted', 'processing', 'error'].includes(i.status));

  return (
    <div className="cs-page u-stack m-rise" style={{ '--gap': 'var(--sp-6)' }}>
      <div className="cs-page-head">
        <div>
          <h1>Team &amp; access</h1>
          <p className="u-muted">{org?.name || '…'} — who's in, what they can see, and what they can do</p>
        </div>
        <button className="cs-btn cs-btn-primary" onClick={() => setShowInvite(true)}>Invite user</button>
      </div>

      {/* Members */}
      <section className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
        <h2 className="u-small" style={{ letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--muted)' }}>
          Members
        </h2>
        {members === null && <div className="cs-skeleton" style={{ height: 100 }} />}
        <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
          {(members || []).map((m) => (
            <div
              key={m.uid}
              className="cs-card"
              style={{
                padding: 'var(--sp-3) var(--sp-4)',
                cursor: 'pointer',
                borderColor: selectedUid === m.uid ? 'var(--brand)' : undefined,
              }}
              onClick={() => setSelectedUid(m.uid === selectedUid ? null : m.uid)}
            >
              <div className="u-cluster" style={{ '--gap': 'var(--sp-3)', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <strong>{m.displayName || m.email}</strong>
                  <span className="u-small u-muted" style={{ marginLeft: 'var(--sp-2)' }}>{m.email}</span>
                  {m.uid === user?.uid && <span className="u-xs u-muted" style={{ marginLeft: 'var(--sp-2)' }}>(you)</span>}
                </div>
                <span className={`cs-badge ${ROLE_TONE[m.role] || ''}`}>{m.role}</span>
                {m.role === 'member' && m.canInvite && <span className="cs-chip" title="Can invite outside people to projects they admin">can invite</span>}
                <span className="u-xs u-muted u-tabular">joined {fmtWhen(m.createdAt)}</span>
                {m.role !== 'owner' && m.uid !== user?.uid && (
                  <span className="u-cluster" style={{ '--gap': 'var(--sp-2)' }} onClick={(e) => e.stopPropagation()}>
                    {m.role === 'member' && (
                      <label className="u-xs u-cluster" style={{ '--gap': '4px', whiteSpace: 'nowrap' }} title="Lets this member invite outside emails onto projects where they are a project admin — without seeing anything else">
                        <input
                          type="checkbox"
                          checked={m.canInvite === true}
                          onChange={(e) => setCanInvite(m, e.target.checked)}
                          aria-label={`Allow ${m.email} to invite people to their projects`}
                        />
                        can invite
                      </label>
                    )}
                    <select
                      className="cs-input"
                      style={{ width: 110, padding: '2px 6px' }}
                      value={m.role}
                      onChange={(e) => setOrgRole(m, e.target.value)}
                      aria-label={`Org role for ${m.email}`}
                    >
                      <option value="member">member</option>
                      <option value="admin">admin</option>
                    </select>
                    <button type="button" className="cs-btn cs-btn-sm" onClick={() => removeMember(m)}>Remove</button>
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
        <p className="u-xs u-muted">
          The owner sees every project. Admins see every project except ones marked <em>restricted</em> on their Team tab
          (those they must be added to like anyone else). Members see only the projects granted below — click a member to
          manage their access. “Can invite” lets a member invite outside emails onto projects where they are a project admin.
        </p>
      </section>

      {/* Access matrix for the selected member */}
      {selected && (
        <section className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
          <h2 className="u-small" style={{ letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--muted)' }}>
            Project access — {selected.displayName || selected.email}
          </h2>
          <AccessMatrix orgId={orgId} member={selected} deals={deals || []} />
        </section>
      )}

      {/* Invites */}
      <section className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
        <h2 className="u-small" style={{ letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--muted)' }}>
          Invites
        </h2>
        {pendingInvites.length === 0 && <span className="u-small u-muted">No open invites.</span>}
        <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
          {pendingInvites.map((inv) => (
            <div key={inv.id} className="cs-card" style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
              <div className="u-cluster" style={{ '--gap': 'var(--sp-3)', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <strong className="u-small">{inv.email}</strong>
                  <span className="u-xs u-muted" style={{ marginLeft: 'var(--sp-2)' }}>
                    as {inv.role}
                    {(inv.projects || []).length ? ` · ${inv.projects.length} project${inv.projects.length > 1 ? 's' : ''}` : ''}
                  </span>
                </div>
                {inv.emailError && <span className="cs-badge b-danger" title={inv.emailError}>email failed</span>}
                {inv.emailedAt && !inv.emailError && <span className="cs-badge b-ok">emailed</span>}
                <span className="cs-badge">{inv.status}</span>
                <span className="u-xs u-muted u-tabular">sent {fmtWhen(inv.createdAt)}</span>
                {inv.status === 'pending' && (
                  <button type="button" className="cs-btn cs-btn-sm" onClick={() => revokeInvite(inv)}>Revoke</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Letterhead theme for branded exports */}
      <section className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
        <h2 className="u-small" style={{ letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--muted)' }}>
          Letterhead
        </h2>
        <LetterheadCard orgId={orgId} org={org} />
      </section>

      {/* Customer-authorized support access (lockbox) */}
      <section className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
        <h2 className="u-small" style={{ letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--muted)' }}>
          Support access
        </h2>
        <SupportAccessCard orgId={orgId} role={role} />
      </section>

      {/* OAuth grants (ChatGPT / MCP connectors) for the signed-in user */}
      <section className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
        <h2 className="u-small" style={{ letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--muted)' }}>
          Connected apps
        </h2>
        <ConnectedAppsCard user={user} />
      </section>

      {showInvite && <InviteModal org={org} orgId={orgId} deals={deals || []} onClose={() => setShowInvite(false)} />}
    </div>
  );
}
